import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation, useOutlet, Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import { showHtdLoginSuccess } from '../utils/htdLoginSuccess'
import type { TenantThemeConfig } from '../types/theme'
import { getThemeById } from '../config/themes'
import * as authService from '../services/authService'
import * as walletService from '../services/walletService'
import { USER_SUMMARY_UPDATED_EVENT } from '../services/pointsService'
import truemoneyLogo from '@/assets/truemoney-logo.png'
import { OpenDWContext } from '../context/OpenDWContext'
import { useSingleTabEnforcement } from '../hooks/useSingleTabEnforcement'
import { useSessionReplacedPoll } from '../hooks/useSessionReplacedPoll'
import { useActivityTracker } from '../hooks/useActivityTracker'
import { SessionWarningModal } from './SessionWarningModal'
import ChatWidget from './ChatWidget'
import DraggableLiveTvFloat from './DraggableLiveTvFloat'
import DraggablePredictionFloat from './DraggablePredictionFloat'
import BottomDepositFlowModal from './BottomDepositFlowModal'
import { InfoModal } from './InfoModal'
import { HtdBottomNavIcon } from './HtdBottomNavIcons'
import {
  getDefaultWithdrawalSettings,
  getWithdrawalSettings,
  type WithdrawalSettings,
} from '../services/withdrawalSettingsService'
import { createWithdrawal } from '../services/walletTransactionService'

const DEFAULT_MARQUEE_FALLBACK =
  'ยินดีต้อนรับทุกท่านเข้าสู่เว็บ lenhuay.net เว็บหวยออนไลน์ที่มาแรงที่สุดในตอนนี้ มั่นคง ซื่อตรง รับประกันจ่ายจริงทุกโพย'

function translateWithdrawalErrorMessage(errorMessage: string): string {
  const normalizedMessage = String(errorMessage || '').trim()
  if (!normalizedMessage) {
    return 'เกิดข้อผิดพลาดในการถอนเงิน'
  }

  const minimumMatch = normalizedMessage.match(/^Minimum withdrawal amount is\s+(\d+(?:\.\d+)?)$/i)
  if (minimumMatch) {
    return `ยอดถอนขั้นต่ำคือ ${walletService.formatCurrency(Number(minimumMatch[1]))} บาท`
  }

  const maximumMatch = normalizedMessage.match(/^Maximum withdrawal amount per transaction is\s+(\d+(?:\.\d+)?)$/i)
  if (maximumMatch) {
    return `ยอดถอนสูงสุดต่อครั้งคือ ${walletService.formatCurrency(Number(maximumMatch[1]))} บาท`
  }

  const dailyLimitMatch = normalizedMessage.match(/^Daily withdrawal request limit exceeded\s*\((\d+)\s+per day\)$/i)
  if (dailyLimitMatch) {
    return `คุณทำรายการถอนครบจำนวน ${dailyLimitMatch[1]} ครั้งต่อวันแล้ว`
  }

  if (/^HTTP(?:\s+error!?\s*status:)?\s*\d+/i.test(normalizedMessage) || /^Failed to fetch$/i.test(normalizedMessage)) {
    return 'ไม่สามารถเชื่อมต่อระบบถอนได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง'
  }

  const mappedMessages: Record<string, string> = {
    'Validation failed': 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบแล้วลองใหม่อีกครั้ง',
    'Tenant ID and User ID are required': 'ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่อีกครั้ง',
    'Invalid amount': 'จำนวนเงินถอนไม่ถูกต้อง',
    'Wallet not found': 'ไม่พบบัญชีกระเป๋าเงิน กรุณาติดต่อแอดมิน',
    'Withdrawal blocked because bonus turnover or bonus conditions are not complete': 'ยังมีเงื่อนไขโบนัสหรือ turnover ที่ต้องทำให้ครบก่อนถอนเงิน',
    'Withdrawal blocked because cashback turnover is not complete': 'ยังมีเงื่อนไข cashback turnover ที่ต้องทำให้ครบก่อนถอนเงิน',
    'Withdrawal requires bonus forfeiture before it can continue': 'รายการถอนนี้ต้องสละสิทธิ์โบนัสก่อนจึงจะดำเนินการต่อได้',
    'Insufficient wallet balance': 'ยอดเงินในกระเป๋าไม่เพียงพอ',
    'Invalid withdrawal amount': 'จำนวนเงินถอนไม่ถูกต้อง',
    'Unable to load withdrawal settings': 'ไม่สามารถโหลดเงื่อนไขการถอนได้ กรุณาลองใหม่อีกครั้ง',
  }

  return mappedMessages[normalizedMessage] || normalizedMessage
}

function getWithdrawalApprovalStatusText(amountNumber: number, withdrawalSettings: WithdrawalSettings): string {
  const autoApproveDisabled = withdrawalSettings.autoWithdrawalConditions.autoApproveEnabled === false
  const maxAutoApprovalAmount = Number(withdrawalSettings.autoWithdrawalConditions.maxAutoApprovalAmount || 0)
  const exceedsAutoApprovalLimit =
    maxAutoApprovalAmount >= 0 && amountNumber > maxAutoApprovalAmount

  if (autoApproveDisabled) {
    return 'ระบบปิดการอนุมัติอัตโนมัติ และรายการนี้จะรอเจ้าหน้าที่อนุมัติ'
  }

  if (exceedsAutoApprovalLimit) {
    return `ยอดนี้เกิน ${walletService.formatCurrency(maxAutoApprovalAmount)} บาท และจะรอเจ้าหน้าที่อนุมัติ`
  }

  return 'อยู่ในเกณฑ์ดำเนินการอัตโนมัติ'
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      marquee: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        direction?: string
      }
    }
  }
}

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernLayoutProps {
  /** เมื่อใช้กับ React Router จะใช้ useOutlet() แทน; ไม่ใช้ Router ได้ส่ง children */
  children?: React.ReactNode
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
  /** Sections แถวเต็มความกว้าง (col-12) ด้านล่าง — ตรง template: หวยชุด, หวยต่างประเทศ */
  bottomSections?: React.ReactNode[]
}

/** Placeholder เมื่อโหลดรูปไม่สำเร็จ — ใช้ data URI เพื่อไม่ให้ onError เกิดซ้ำ (ลูป) */
const IMG_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

const getActiveNavItemForPath = (path: string): number => {
  if (path === '/' || path === '') return 0
  if (path === '/topup' || path.startsWith('/topup/')) return 1
  if (path === '/dashboard') return 2
  if (path === '/withdraw') return 3
  if (path === '/promotion') return 4
  return 0
}

/** path ตาม cdn-assets: /img/line_icon.png, /css/... (แบบ user-classic) */
const ModernLayout: React.FC<ModernLayoutProps> = ({ children, themeConfig, assetConfig, bottomSections = [] }) => {
  const [activeNavItem, setActiveNavItem] = useState(0)
  const [authRefresh, setAuthRefresh] = useState(0)
  const [loginUsername, setLoginUsername] = useState(() =>
    typeof localStorage !== 'undefined' ? localStorage.getItem('savedUsername') || '' : ''
  )
  const [loginPassword, setLoginPassword] = useState('')
  const [loginRememberMe, setLoginRememberMe] = useState(() =>
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('rememberMe') !== 'false' || !!localStorage.getItem('savedUsername')
      : false
  )
  const [loginError, setLoginError] = useState<string | null>(null)
  const [loginLoading, setLoginLoading] = useState(false)
  const [userInfoSummary, setUserInfoSummary] = useState<authService.UserInfoSummary | null>(null)
  const [walletData, setWalletData] = useState<walletService.WalletData | null>(null)
  const [isLoadingBalanceAndPoints, setIsLoadingBalanceAndPoints] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userBankAccounts, setUserBankAccounts] = useState<authService.UserBankAccount[]>([])
  const [withdrawAmountInput, setWithdrawAmountInput] = useState('')
  const [isSubmittingWithdrawPopup, setIsSubmittingWithdrawPopup] = useState(false)
  const [isLoadingWithdrawSettings, setIsLoadingWithdrawSettings] = useState(false)
  const [withdrawalSettings, setWithdrawalSettings] = useState<WithdrawalSettings>(getDefaultWithdrawalSettings())
  const [showPaymentRatesModal, setShowPaymentRatesModal] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()
  const authenticated = authService.isAuthenticated()
  const hasAuthSession = authenticated || Boolean(authService.getRefreshToken())
  const user = authService.getCurrentUser()
  const accessAlertKeyRef = useRef<string | null>(null)
  const { isDuplicateTab, takeOverSession } = useSingleTabEnforcement(authenticated)
  useSessionReplacedPoll(hasAuthSession)

  const loadBalanceAndPoints = useCallback(() => {
    if (!authenticated) {
      setWalletData(null)
      setUserInfoSummary(null)
      return Promise.resolve()
    }

    setIsLoadingBalanceAndPoints(true)
    return Promise.all([
      walletService.getWallet(),
      authService.fetchUserInfoSummary(),
    ])
      .then(([walletRes, summary]) => {
        if (walletRes.success && walletRes.data) setWalletData(walletRes.data)
        else setWalletData(null)
        setUserInfoSummary(summary)
      })
      .finally(() => {
        setIsLoadingBalanceAndPoints(false)
      })
  }, [authenticated])

  // Idle logout แบบเดียวกับ user-classic: ปรับได้ตาม tenant (features.sessionIdleMinutes 0–120, default 30, 0 = ปิด)
  const sessionIdleMinutes = themeConfig.settings?.sessionIdleMinutes ?? 30
  const activityTrackingEnabled = authenticated && sessionIdleMinutes > 0
  const {
    showSessionWarning,
    timeRemaining,
    extendSession,
    handleLogout: sessionWarningLogout,
  } = useActivityTracker({
    inactivityTimeout: sessionIdleMinutes * 60 * 1000,
    warningTimeout: 5 * 60 * 1000, // เตือนก่อน 5 นาที
    enabled: activityTrackingEnabled,
    onLogout: async () => {
      await authService.logout()
      navigate('/login', { replace: true })
    },
    onExtendSession: async () => {
      const refreshed = await authService.refreshToken()
      if (!refreshed.success) {
        throw new Error('Token refresh failed')
      }
    },
  })

  // ดึงยอดเครดิตจาก Wallet API และคะแนนจาก user-info (แบบเดียวกับ user-classic)
  useEffect(() => {
    void loadBalanceAndPoints()
  }, [authRefresh, loadBalanceAndPoints])

  // เมื่อแอดมินเติม/หักเครดิต WebSocket ส่ง balance_notification → รีเฟรชยอดใน header
  useEffect(() => {
    const handleBalanceNotification = () => {
      if (authenticated) {
        walletService.getWallet().then((res) => {
          if (res.success && res.data) setWalletData(res.data)
        })
      }
    }
    window.addEventListener('balance_notification', handleBalanceNotification)
    return () => window.removeEventListener('balance_notification', handleBalanceNotification)
  }, [authenticated])

  useEffect(() => {
    const handleUserSummaryUpdated = () => {
      if (authenticated) {
        authService.fetchUserInfoSummary({ force: true }).then(setUserInfoSummary)
      }
    }
    window.addEventListener(USER_SUMMARY_UPDATED_EVENT, handleUserSummaryUpdated)
    return () => window.removeEventListener(USER_SUMMARY_UPDATED_EVENT, handleUserSummaryUpdated)
  }, [authenticated])

  useEffect(() => {
    const handleUserServiceAccessUpdated = () => {
      if (authenticated) {
        authService.fetchUserInfoSummary().then(setUserInfoSummary)
      }
    }

    window.addEventListener('user_service_access_updated', handleUserServiceAccessUpdated)
    return () => window.removeEventListener('user_service_access_updated', handleUserServiceAccessUpdated)
  }, [authenticated])

  useEffect(() => {
    if (!authenticated) {
      accessAlertKeyRef.current = null
      return
    }
    if (!userInfoSummary) return

    const path = location.pathname || '/'
    const isLotteryPath =
      path === '/lobby-huay' ||
      path.startsWith('/lobby-huay/listall') ||
      path.startsWith('/lobby-huay/lottery-terms') ||
      path.startsWith('/lobby-huay/bet') ||
      path.startsWith('/lobby-huay/yeekee') ||
      path.startsWith('/lobby-huay/set') ||
      path.startsWith('/lobby-huay/win-number')
    const isCasinoPath = path === '/lobby-casino'

    // ปิดทั้งระบบระดับ tenant (ปิดปรับปรุงระบบ) มาก่อนสิทธิ์ระดับ user
    const tLottery = themeConfig?.settings?.lotteryEnabled !== false
    const tCasino = themeConfig?.settings?.casinoEnabled !== false

    let blockedService: 'lottery' | 'casino' | null = null
    let blockedReason: 'tenant' | 'user' = 'user'
    if (isLotteryPath) {
      if (!tLottery) {
        blockedService = 'lottery'
        blockedReason = 'tenant'
      } else if (!authService.isManagedServiceEnabled(userInfoSummary.serviceConfigs, 'lottery')) {
        blockedService = 'lottery'
        blockedReason = 'user'
      }
    } else if (isCasinoPath) {
      if (!tCasino) {
        blockedService = 'casino'
        blockedReason = 'tenant'
      } else if (!authService.isManagedServiceEnabled(userInfoSummary.serviceConfigs, 'casino')) {
        blockedService = 'casino'
        blockedReason = 'user'
      }
    }

    if (!blockedService) {
      accessAlertKeyRef.current = null
      return
    }

    const alertKey = `${blockedService}:${blockedReason}:${path}`
    if (accessAlertKeyRef.current === alertKey) return
    accessAlertKeyRef.current = alertKey

    void Swal.fire({
      icon: 'warning',
      title: blockedReason === 'tenant'
        ? 'ปิดปรับปรุงระบบ'
        : (blockedService === 'lottery' ? 'ปิดการใช้งานหวย' : 'ปิดการใช้งานเกมส์'),
      text:
        blockedReason === 'tenant'
          ? (blockedService === 'lottery'
              ? 'ขณะนี้ระบบหวยปิดปรับปรุงชั่วคราว กรุณาลองใหม่อีกครั้งภายหลัง'
              : 'ขณะนี้ระบบเกมส์ปิดปรับปรุงชั่วคราว กรุณาลองใหม่อีกครั้งภายหลัง')
          : (blockedService === 'lottery'
              ? 'บัญชีนี้ถูกปิดสิทธิ์การเข้าใช้งานหวยชั่วคราว กรุณาติดต่อแอดมิน'
              : 'บัญชีนี้ถูกปิดสิทธิ์การเข้าใช้งานเกมส์ชั่วคราว กรุณาติดต่อแอดมิน'),
      confirmButtonText: 'ตกลง',
    }).finally(() => {
      navigate('/dashboard', { replace: true })
    })
  }, [authenticated, location.pathname, navigate, userInfoSummary, themeConfig?.settings?.lotteryEnabled, themeConfig?.settings?.casinoEnabled])

  // ปิดทั้งระบบระดับ tenant (ปิดปรับปรุงระบบ)
  const tenantLotteryEnabled = themeConfig?.settings?.lotteryEnabled !== false
  const tenantCasinoEnabled = themeConfig?.settings?.casinoEnabled !== false
  // สิทธิ์ระดับ user (admin ปิดเฉพาะบัญชี)
  const userLotteryEnabled = authService.isManagedServiceEnabled(userInfoSummary?.serviceConfigs, 'lottery')
  const userCasinoEnabled = authService.isManagedServiceEnabled(userInfoSummary?.serviceConfigs, 'casino')
  // เข้าเล่นได้จริงเมื่อเปิดทั้ง tenant และ user
  const isLotteryEnabled = tenantLotteryEnabled && userLotteryEnabled
  const isCasinoEnabled = tenantCasinoEnabled && userCasinoEnabled
  const withdrawEnabled = themeConfig?.settings?.withdrawEnabled !== false

  const themeFolder = useMemo(() => {
    const meta = getThemeById(themeConfig.themeId)
    return meta?.folder ?? 'HuayToday-Blue'
  }, [themeConfig.themeId])

  const getAssetUrl = (path: string) => {
    const p = path.startsWith('/') ? path : `/${path}`
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
    return base + p
  }

  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const el = e.currentTarget
    if (el.dataset.imgFallback === '1') return
    el.dataset.imgFallback = '1'
    el.src = IMG_PLACEHOLDER
  }

  // Extract configuration from themeConfig
  const { branding } = themeConfig.theme
  const { marqueeText, paymentRates } = themeConfig.settings
  const displayMarquee = marqueeText || DEFAULT_MARQUEE_FALLBACK
  const isFromApi = Boolean(marqueeText && marqueeText !== DEFAULT_MARQUEE_FALLBACK)
  const openPaymentRatesModal = useCallback(() => setShowPaymentRatesModal(true), [])
  const closePaymentRatesModal = useCallback(() => setShowPaymentRatesModal(false), [])

  useEffect(() => {
    const onHtdOpenPaymentRates = () => openPaymentRatesModal()
    window.addEventListener('htd-open-payment-rates', onHtdOpenPaymentRates)
    return () => window.removeEventListener('htd-open-payment-rates', onHtdOpenPaymentRates)
  }, [openPaymentRatesModal])

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('📢 [ModernLayout] marquee:', {
        fromApi: isFromApi,
        hasMarqueeText: Boolean(marqueeText),
        preview: displayMarquee.slice(0, 80) + (displayMarquee.length > 80 ? '...' : ''),
        rawLength: marqueeText?.length ?? 0,
      })
    }
  }, [marqueeText, displayMarquee, isFromApi])

  // โหลดบัญชีธนาคารของผู้ใช้ (สำหรับ popup ถอน + เงื่อนไขฝาก/ถอน)
  const loadUserBankAccounts = useCallback(() => {
    if (!authenticated) {
      setUserBankAccounts([])
      return
    }
    authService.fetchUserBankAccounts().then((list) => {
      setUserBankAccounts(list ?? [])
    })
  }, [authenticated])
  useEffect(() => {
    loadUserBankAccounts()
  }, [loadUserBankAccounts])

  /** โลโก้ธนาคารสำหรับบัญชีของผู้ใช้ (จาก user-info) */
  const getUserBankLogoUrl = useCallback((account: authService.UserBankAccount): string => {
    if (account.imageUrl) return account.imageUrl
    const code = (account.bankCode || '').toUpperCase()
    const name = (account.bankName || '').toLowerCase()
    if (code === 'TRUE' || name.includes('true money')) return typeof truemoneyLogo === 'string' ? truemoneyLogo : (truemoneyLogo as unknown as string)
    return getAssetUrl(`/img/b/${code || 'KBANK'}.png`)
  }, [themeFolder, assetConfig.CDN_BASE_URL])

  const primaryUserBankAccount = useMemo(
    () => userBankAccounts[0] ?? null,
    [userBankAccounts]
  )

  // เมนูล่าง: SVG icons + ข้อความ (ฝาก/ถอน กดแล้วเปิด popup ตามเดิม)
  const navigationItems = [
    { text: 'หน้าแรก', id: 0 },
    { text: 'ฝากเงิน', id: 1 },
    { text: 'ศูนย์ควบคุม', id: 2 },
    { text: 'ถอนเงิน', id: 3 },
    { text: 'โปรโมชั่น', id: 4 },
  ]

  // Popup ฝากเงิน/ถอนเงิน (แบบ member.html)
  const [openDWPopup, setOpenDWPopup] = useState<'deposit' | 'withdraw' | null>(null)
  // เมื่อเปิด popup ฝาก/ถอน ให้โหลดบัญชีใหม่ (เผื่อผู้ใช้เพิ่งกรอกบัญชีที่หน้า Member)
  useEffect(() => {
    if (openDWPopup === 'deposit' || openDWPopup === 'withdraw') loadUserBankAccounts()
  }, [openDWPopup, loadUserBankAccounts])

  useEffect(() => {
    if (openDWPopup !== 'withdraw' || !authenticated) return
    setWithdrawAmountInput('')
    setIsLoadingWithdrawSettings(true)
    getWithdrawalSettings()
      .then((settings) => setWithdrawalSettings(settings))
      .finally(() => setIsLoadingWithdrawSettings(false))
  }, [authenticated, openDWPopup])

  // Sync activeNavItem กับ route: หน้าแรก, ฝากเงิน, ศูนย์ควบคุม, ถอนเงิน, โปรโมชั่น
  useEffect(() => {
    const path = location.pathname || '/'
    setActiveNavItem(getActiveNavItemForPath(path))
  }, [location.pathname])

  const handleNavItemClick = useCallback(
    (itemId: number) => {
      setActiveNavItem(itemId)
      if (itemId === 0) {
        navigate('/')
        setOpenDWPopup(null)
      } else if (itemId === 1) {
        // TEMP: ข้าม popup ฝากหลายขั้นตอน — ไปหน้า /topup โดยตรง (รอ owner สรุป flow ใหม่)
        navigate('/topup')
        setOpenDWPopup(null)
      } else if (itemId === 2) {
        navigate('/dashboard')
        setOpenDWPopup(null)
      } else if (itemId === 3) {
        if (!withdrawEnabled) {
          navigate('/withdraw')
          setOpenDWPopup(null)
          return
        }
        setOpenDWPopup('withdraw')
      } else if (itemId === 4) {
        navigate('/promotion')
        setOpenDWPopup(null)
      } else {
        setOpenDWPopup(null)
      }
    },
    [navigate, withdrawEnabled]
  )

  useEffect(() => {
    if (!withdrawEnabled && openDWPopup === 'withdraw') {
      setOpenDWPopup(null)
    }
  }, [withdrawEnabled, openDWPopup])

  const closeDWPopup = useCallback(() => {
    setOpenDWPopup(null)
    setWithdrawAmountInput('')
    setIsSubmittingWithdrawPopup(false)
    setActiveNavItem(getActiveNavItemForPath(location.pathname || '/'))
  }, [location.pathname])

  const checkWithdrawPopupConditions = useCallback(() => {
    const currentTime = new Date()
    const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes()
    const [shutdownHour, shutdownMinute] = withdrawalSettings.withdrawalBot.shutdownTime.split(':').map(Number)
    const [activationHour, activationMinute] = withdrawalSettings.withdrawalBot.activationTime.split(':').map(Number)
    const shutdownMinutes = shutdownHour * 60 + shutdownMinute
    const activationMinutes = activationHour * 60 + activationMinute

    let isBotActive = true
    if (shutdownMinutes > activationMinutes) {
      isBotActive = currentMinutes >= activationMinutes && currentMinutes < shutdownMinutes
    } else {
      isBotActive = currentMinutes >= activationMinutes || currentMinutes < shutdownMinutes
    }

    if (!isBotActive) {
      return {
        isValid: false,
        message: `ระบบถอนเงินปิดชั่วคราว เปิดให้บริการเวลา ${withdrawalSettings.withdrawalBot.shutdownTime} - ${withdrawalSettings.withdrawalBot.activationTime}`,
      }
    }

    return { isValid: true }
  }, [withdrawalSettings])

  const handleWithdrawPopupSubmit = useCallback(async () => {
    const amountNumber = Number(withdrawAmountInput)
    const availableBalance = walletData?.balance?.main || 0
    const primaryBank = primaryUserBankAccount
    const accountName = primaryBank?.accountName ?? '–'
    const accountNumber = primaryBank?.accountNumber ?? '–'
    const bankName = primaryBank?.bankName ?? '–'

    if (!withdrawEnabled) {
      await Swal.fire({
        icon: 'warning',
        title: 'ปิดการถอนเงินชั่วคราว',
        text: 'ขณะนี้ระบบปิดการถอนเงิน กรุณาติดต่อแอดมินหรือลองใหม่ในภายหลัง',
        confirmButtonColor: '#0d6efd',
      })
      closeDWPopup()
      return
    }

    if (!withdrawAmountInput || !Number.isFinite(amountNumber) || amountNumber <= 0) {
      await Swal.fire({ icon: 'warning', title: 'กรุณาระบุจำนวนเงินที่ต้องการถอน', confirmButtonColor: '#0d6efd' })
      return
    }

    if (isLoadingWithdrawSettings) {
      await Swal.fire({
        icon: 'info',
        title: 'กำลังโหลดการตั้งค่า',
        text: 'กรุณารอสักครู่แล้วลองใหม่อีกครั้ง',
        confirmButtonColor: '#0d6efd',
      })
      return
    }

    if (!primaryBank) {
      await Swal.fire({
        icon: 'warning',
        title: 'ยังไม่พบบัญชีธนาคาร',
        text: 'กรุณาเพิ่มบัญชีธนาคารก่อนทำรายการถอนเงิน',
        confirmButtonColor: '#0d6efd',
      })
      closeDWPopup()
      navigate('/member')
      return
    }

    if (amountNumber < withdrawalSettings.withdrawalConditions.minWithdrawalAmount) {
      await Swal.fire({
        icon: 'warning',
        title: 'จำนวนเงินไม่ถูกต้อง',
        text: `ถอนเงินขั้นต่ำ ${walletService.formatCurrency(withdrawalSettings.withdrawalConditions.minWithdrawalAmount)} บาท`,
        confirmButtonColor: '#0d6efd',
      })
      return
    }

    if (
      withdrawalSettings.autoWithdrawalConditions.maxWithdrawalPerTransaction > 0
      && amountNumber > withdrawalSettings.autoWithdrawalConditions.maxWithdrawalPerTransaction
    ) {
      await Swal.fire({
        icon: 'warning',
        title: 'ยอดถอนเกินกำหนด',
        text: `ถอนได้ไม่เกิน ${walletService.formatCurrency(withdrawalSettings.autoWithdrawalConditions.maxWithdrawalPerTransaction)} บาท ต่อครั้ง`,
        confirmButtonColor: '#0d6efd',
      })
      return
    }

    if (amountNumber > availableBalance) {
      await Swal.fire({
        icon: 'error',
        title: 'ยอดเงินไม่เพียงพอ',
        text: `ยอดเงินคงเหลือ ${walletService.formatCurrency(availableBalance)} บาท`,
        confirmButtonColor: '#0d6efd',
      })
      return
    }

    const conditionCheck = checkWithdrawPopupConditions()
    if (!conditionCheck.isValid) {
      await Swal.fire({
        icon: 'error',
        title: 'ไม่สามารถถอนเงินได้',
        text: conditionCheck.message,
        confirmButtonColor: '#0d6efd',
      })
      return
    }

    const requiresManualApproval =
      withdrawalSettings.autoWithdrawalConditions.autoApproveEnabled === false
      || (
        withdrawalSettings.autoWithdrawalConditions.maxAutoApprovalAmount >= 0
        && amountNumber > withdrawalSettings.autoWithdrawalConditions.maxAutoApprovalAmount
      )

    const confirmed = await Swal.fire({
      icon: 'question',
      title: 'ยืนยันการถอนเงิน',
      html: `
        <div style="text-align:left">
          <p><strong>จำนวนเงิน:</strong> ${walletService.formatCurrency(amountNumber)} บาท</p>
          <p><strong>ธนาคาร:</strong> ${bankName}</p>
          <p><strong>เลขบัญชี:</strong> ${accountNumber}</p>
          <p><strong>ชื่อบัญชี:</strong> ${accountName}</p>
          <p><strong>สถานะ:</strong> ${getWithdrawalApprovalStatusText(amountNumber, withdrawalSettings)}</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'ยืนยันถอนเงิน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#0d6efd',
      cancelButtonColor: '#6b7280',
    })

    if (!confirmed.isConfirmed) return

    setIsSubmittingWithdrawPopup(true)
    try {
      const response = await createWithdrawal({
        amount: amountNumber,
        bankAccount: {
          bankCode: primaryBank.bankCode,
          accountNumber: primaryBank.accountNumber,
          accountName: primaryBank.accountName,
        },
        reason: 'user_withdrawal_request',
      })

      if (!response.success) {
        if (response.data?.bonuses?.length) {
          const bonusItems = response.data.bonuses
            .map((bonus) => {
              const remaining = Number(bonus.wageringRemaining || 0)
              const amount = Number(bonus.amount || 0)
              const note = String(bonus.note || '').trim()
              return `<li style="margin-bottom:8px;"><strong>${bonus.type || 'โบนัส'}</strong> วงเงิน ${walletService.formatCurrency(amount)} บาท, turnover คงเหลือ ${walletService.formatCurrency(remaining)} บาท${note ? `<br/><span style="color:#6b7280;">${note}</span>` : ''}</li>`
            })
            .join('')

          await Swal.fire({
            icon: 'warning',
            title: 'ยังถอนเงินไม่ได้',
            html: `
              <div style="text-align:left">
                <p>${translateWithdrawalErrorMessage(response.message || response.error || 'ยังมีเงื่อนไขโบนัสหรือ turnover ที่ต้องทำให้ครบก่อนถอนเงิน')}</p>
                <p style="margin-top:12px;"><strong>turnover คงเหลือรวม:</strong> ${walletService.formatCurrency(Number(response.data.totalRemainingWagering || 0))} บาท</p>
                <ul style="padding-left:18px;margin-top:12px;">${bonusItems}</ul>
              </div>
            `,
            confirmButtonColor: '#0d6efd',
          })
          return
        }
        throw new Error(response.message || response.error || 'ไม่สามารถส่งคำขอถอนเงินได้')
      }

      await loadBalanceAndPoints()
      setWithdrawAmountInput('')
      closeDWPopup()

      await Swal.fire({
        icon: 'success',
        title: 'ส่งคำขอถอนเงินสำเร็จ',
        text: requiresManualApproval
          ? `คำขอถอนเงิน ${walletService.formatCurrency(amountNumber)} บาท ถูกส่งแล้วและกำลังรอเจ้าหน้าที่อนุมัติ`
          : `คำขอถอนเงิน ${walletService.formatCurrency(amountNumber)} บาท ถูกส่งเรียบร้อยแล้ว`,
        confirmButtonColor: '#0d6efd',
      })

      navigate('/history/withdraw')
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'ถอนเงินไม่สำเร็จ',
        text: translateWithdrawalErrorMessage(error instanceof Error ? error.message : ''),
        confirmButtonColor: '#0d6efd',
      })
    } finally {
      setIsSubmittingWithdrawPopup(false)
    }
  }, [
    checkWithdrawPopupConditions,
    closeDWPopup,
    isLoadingWithdrawSettings,
    loadBalanceAndPoints,
    navigate,
    primaryUserBankAccount,
    walletData?.balance?.main,
    withdrawAmountInput,
    withdrawalSettings,
    withdrawEnabled,
  ])

  const outlet = useOutlet()
  const isDashboard = location.pathname === '/dashboard'
  const isLobbyCasino = location.pathname === '/lobby-casino'
  const isPromotion = location.pathname === '/promotion'
  const isLobbyHuay = location.pathname === '/lobby-huay' || location.pathname.startsWith('/lobby-huay/')
  const isHistory = location.pathname === '/history' || location.pathname.startsWith('/history/')
  const isAffiliate = location.pathname === '/affiliate' || location.pathname.startsWith('/affiliate/')
  const isCashback = location.pathname === '/cashback'
  const isMember = location.pathname === '/member'
  const isReward = location.pathname === '/reward' || location.pathname.startsWith('/reward/')
  const isHowto = location.pathname === '/howto'
  const isContact = location.pathname === '/contact'
  const isTopupFlowPage =
    location.pathname === '/topup'
    || location.pathname.startsWith('/topup/confirm')
    || location.pathname.startsWith('/topup/slip')
  const isWithdraw = location.pathname === '/withdraw'
  const isPoints = location.pathname === '/points' || location.pathname.startsWith('/points/')
  const isLiveTv = location.pathname === '/live-tv'
  /** หน้าแรก HTD — full width ให้ HtdHomePage คุม layout ทั้งหน้า */
  const isHome = location.pathname === '/' || location.pathname === ''
  /** หน้าแจ้งฝากเงิน (จองโอน) — ไม่แสดง header / มาร์ควี / ยอดเครดิต / ปุ่มด้านบน */
  const isDepositReservePage = location.pathname === '/topup/reserve' || location.pathname.startsWith('/topup/reserve/')
  const isFullWidthPage = isHome || isDashboard || isLobbyCasino || isPromotion || isLobbyHuay || isHistory || isAffiliate || isCashback || isMember || isReward || isHowto || isContact || isTopupFlowPage || isWithdraw || isPoints || isLiveTv

  const openDWValue = useMemo(
    () => ({
      openDeposit: () => setOpenDWPopup('deposit'),
      openWithdraw: () => {
        if (!withdrawEnabled) {
          navigate('/withdraw')
          setOpenDWPopup(null)
          return
        }
        setOpenDWPopup('withdraw')
      },
    }),
    [navigate, withdrawEnabled]
  )

  // แปลงข้อความ error จาก API เป็นภาษาไทย (ให้ตรงกับ user-classic)
  const getThaiErrorMessage = useCallback((errorMessage: string): string => {
    const errorMappings: Record<string, string> = {
      'Account is not active': 'บัญชีของคุณถูกปิดใช้งาน กรุณาติดต่อผู้ดูแลระบบ',
      'Account has been disabled by administrator': 'บัญชีของคุณถูกปิดใช้งานโดยผู้ดูแลระบบ',
      'Account is temporarily locked due to too many failed login attempts': 'บัญชีของคุณถูกล็อคชั่วคราวเนื่องจากเข้าสู่ระบบผิดหลายครั้ง กรุณารอสักครู่แล้วลองใหม่',
      'Invalid credentials': 'ชื่อผู้ใช้หรือเบอร์โทรศัพท์หรือรหัสผ่านไม่ถูกต้อง',
      'User not found': 'ไม่พบผู้ใช้ในระบบ',
      'Account is locked': 'บัญชีของคุณถูกล็อค กรุณาติดต่อผู้ดูแลระบบ',
      'Too many login attempts': 'พยายามเข้าสู่ระบบมากเกินไป กรุณารอสักครู่แล้วลองใหม่',
      'Login failed': 'เข้าสู่ระบบไม่สำเร็จ กรุณาตรวจสอบชื่อผู้ใช้หรือเบอร์โทรศัพท์และรหัสผ่าน',
      'Network error': 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาลองใหม่อีกครั้ง',
      'เกิดข้อผิดพลาดในการเข้าสู่ระบบ กรุณาลองใหม่อีกครั้ง': 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาลองใหม่อีกครั้ง',
    }
    return errorMappings[errorMessage] || errorMessage || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ'
  }, [])

  const handleLoginSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setLoginError(null)
      if (!loginUsername.trim() || !loginPassword) {
        await Swal.fire({
          icon: 'warning',
          title: 'ข้อมูลไม่ครบถ้วน',
          text: 'กรุณากรอกชื่อผู้ใช้หรือเบอร์โทรศัพท์และรหัสผ่าน',
          confirmButtonText: 'ตกลง',
          confirmButtonColor: '#ffc107',
        })
        return
      }
      setLoginLoading(true)
      try {
        const res = await authService.login({
          tenantId: themeConfig.tenantId,
          username: loginUsername.trim(),
          password: loginPassword,
          rememberMe: loginRememberMe,
        })
        if (res.success) {
          if (loginRememberMe) {
            localStorage.setItem('savedUsername', loginUsername.trim())
          } else {
            localStorage.removeItem('savedUsername')
          }
          localStorage.setItem('rememberMe', loginRememberMe ? 'true' : 'false')
          setLoginUsername('')
          setLoginPassword('')
          // Show success first so announcement / auth UI refresh doesn't cover it
          await showHtdLoginSuccess({
            username: loginUsername.trim(),
            timerMs: 1800,
            siteName: themeConfig.theme?.branding?.siteName,
          })
          setAuthRefresh((n) => n + 1)
        } else {
          const thaiMessage = getThaiErrorMessage(res.message || res.error || 'Login failed')
          await Swal.fire({
            icon: 'error',
            title: 'เข้าสู่ระบบไม่สำเร็จ',
            text: thaiMessage,
            confirmButtonText: 'ตกลง',
            confirmButtonColor: '#dc3545',
          })
        }
        // Reconnect WebSocket with JWT so websocket-gateway emits user_online (admin sees online)
        if (res.success) {
          window.dispatchEvent(new CustomEvent('huay_websocket_reconnect'))
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'เกิดข้อผิดพลาด'
        const thaiMessage = getThaiErrorMessage(errorMessage)
        await Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: thaiMessage,
          confirmButtonText: 'ตกลง',
          confirmButtonColor: '#dc3545',
        })
      } finally {
        setLoginLoading(false)
      }
    },
    [loginUsername, loginPassword, loginRememberMe, themeConfig.tenantId, getThaiErrorMessage]
  )

  const handleLogout = useCallback(async () => {
    await authService.logout()
    setAuthRefresh((n) => n + 1)
    navigate('/')
  }, [navigate])

  const getHeaderHref = useCallback(
    (path: string, requiresAuth = false) => {
      if (requiresAuth && !authenticated) {
        return '#/login'
      }
      return `#${path}`
    },
    [authenticated]
  )

  return (
    <OpenDWContext.Provider value={openDWValue}>
    <div className={`modern-layout${isHome ? ' modern-layout--htd-home' : ''}`}>
      {/* Single-tab overlay: กันหลายแท็บ — แท็บที่เปิดทีหลังจะถูกบล็อก */}
      {authenticated && isDuplicateTab && (
        <div className="htd-tab-lock">
          <div className="htd-tab-lock__card">
            <h2>มีการใช้งานจากอีกแท็บหนึ่งอยู่</h2>
            <p>
              เพื่อความปลอดภัย กรุณาใช้แค่หนึ่งแท็บขณะเล่นหรือถอนเงิน
              หากแท็บอื่นปิดไปแล้ว หรือต้องการใช้แท็บนี้แทน กดปุ่มด้านล่าง
            </p>
            <button type="button" onClick={takeOverSession} className="htd-tab-lock__btn">
              ใช้แท็บนี้แทน
            </button>
          </div>
        </div>
      )}

      {/* Fixed Background — หน้าแรก HTD ใช้พื้นของตัวเอง */}
      {!isHome && <div className="bg_show"></div>}
      
      {/* Header — ไม่แสดงบนหน้าแจ้งฝากเงิน (จองโอน) และหน้าแรก HTD */}
      {!isDepositReservePage && !isHome && (isFullWidthPage ? (
        <>
          {/* Desktop: header + link_menu (ตรง profile.html) */}
          <header className="header d-none d-md-block bg_blur ins_sd">
            <div className="container">
              <div className="box_row justify-content-between">
                <a href={getHeaderHref('/')} className="logo_links">
                  <img src={branding.logoUrl} alt={branding.siteName} onError={handleImgError} />
                </a>
                {authenticated ? (
                <div className="link_menu header-desktop-nav">
                  <a href={getHeaderHref('/')} className="link_item header-nav-item text-center c_tertiary text-decoration-none">
                    <h5 className="font_md mb-0">หน้าแรก</h5>
                    <h6 className="mb-0 small opacity-75 header-nav-item__sub">Home</h6>
                  </a>
                  <a href={getHeaderHref('/topup', true)} className="link_item header-nav-item text-center c_tertiary text-decoration-none">
                    <h5 className="font_md mb-0">กระเป๋าเงิน</h5>
                    <h6 className="mb-0 small opacity-75 header-nav-item__sub">Wallet</h6>
                  </a>
                  <a href={getHeaderHref('/promotion', true)} className="link_item header-nav-item text-center c_tertiary text-decoration-none">
                    <h5 className="font_md mb-0">โปรโมชั่น</h5>
                    <h6 className="mb-0 small opacity-75 header-nav-item__sub">Promotion</h6>
                  </a>
                  <a href={getHeaderHref('/affiliate', true)} className="link_item header-nav-item text-center c_tertiary text-decoration-none">
                    <h5 className="font_md mb-0">แนะนำเพื่อน</h5>
                    <h6 className="mb-0 small opacity-75 header-nav-item__sub">Invite Friend</h6>
                  </a>
                  <button
                    type="button"
                    onClick={openPaymentRatesModal}
                    className="link_item header-nav-item payment-rates-cta payment-rates-cta--header-nav text-center text-decoration-none border-0"
                    style={{ cursor: 'pointer' }}
                  >
                    <h5 className="font_md mb-0 payment-rates-cta__label">อัตราการจ่าย</h5>
                    <h6 className="mb-0 small payment-rates-cta__sub header-nav-item__sub">Payment Rates</h6>
                  </button>
                  <a href={getHeaderHref('/contact')} className="link_item header-nav-item text-center c_tertiary text-decoration-none">
                    <h5 className="font_md mb-0">ติดต่อเรา</h5>
                    <h6 className="mb-0 small opacity-75 header-nav-item__sub">Contact Us</h6>
                  </a>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="link_item header-nav-item header-nav-logout text-center text-white text-decoration-none border-0 rounded px-3 py-2"
                    style={{ cursor: 'pointer', backgroundColor: '#dc2626' }}
                  >
                    <h5 className="font_md mb-0 text-white">ออกจากระบบ</h5>
                    <h6 className="mb-0 small opacity-90 text-white header-nav-item__sub">Logout</h6>
                  </button>
                </div>
                ) : null}
              </div>
            </div>
          </header>
          {/* Mobile: header_m + hamburger (ตรง profile.html) */}
          <header className="header_m d-block d-md-none bg_blur ins_sd">
            <div className="container">
              <div className="box_row justify-content-between align-items-center">
                <a href={getHeaderHref('/')} className="logo_links">
                  <img src={branding.logoUrl} alt={branding.siteName} onError={handleImgError} />
                </a>
                {authenticated ? (
                <div>
                  <input
                    type="checkbox"
                    className="menu_btn d-none"
                    id="menuBTN_dashboard"
                    checked={mobileMenuOpen}
                    onChange={(e) => setMobileMenuOpen(e.target.checked)}
                  />
                  <label
                    className="menu_icon"
                    htmlFor="menuBTN_dashboard"
                    style={{ cursor: 'pointer' }}
                    aria-label="เมนู"
                  >
                    <span className="navicon" />
                  </label>
                  <div className={`link_menu_m bg_blur ${mobileMenuOpen ? 'open' : ''}`} style={{ display: mobileMenuOpen ? 'block' : undefined }}>
                    <a href={getHeaderHref('/')} className="link_item text-center c_tertiary text-decoration-none" onClick={() => setMobileMenuOpen(false)}>
                      <h5 className="font_md mb-0">หน้าแรก</h5>
                    </a>
                    <a href={getHeaderHref('/topup', true)} className="link_item text-center c_tertiary text-decoration-none" onClick={() => setMobileMenuOpen(false)}>
                      <h5 className="font_md mb-0">กระเป๋าเงิน</h5>
                    </a>
                    <a href={getHeaderHref('/promotion', true)} className="link_item text-center c_tertiary text-decoration-none" onClick={() => setMobileMenuOpen(false)}>
                      <h5 className="font_md mb-0">โปรโมชั่น</h5>
                    </a>
                    <a href={getHeaderHref('/affiliate', true)} className="link_item text-center c_tertiary text-decoration-none" onClick={() => setMobileMenuOpen(false)}>
                      <h5 className="font_md mb-0">แนะนำเพื่อน</h5>
                    </a>
                    <button
                      type="button"
                      onClick={() => { openPaymentRatesModal(); setMobileMenuOpen(false); }}
                      className="link_item text-center c_tertiary text-decoration-none border-0 bg-transparent w-100"
                      style={{ cursor: 'pointer' }}
                    >
                      <h5 className="font_md mb-0">อัตราการจ่าย</h5>
                    </button>
                    <a href={getHeaderHref('/contact')} className="link_item text-center c_tertiary text-decoration-none" onClick={() => setMobileMenuOpen(false)}>
                      <h5 className="font_md mb-0">ติดต่อเรา</h5>
                    </a>
                    <button
                      type="button"
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="link_item text-center text-white text-decoration-none border-0 w-100 rounded px-3 py-2"
                      style={{ cursor: 'pointer', backgroundColor: '#dc2626' }}
                    >
                      <h5 className="font_md mb-0 text-white">ออกจากระบบ</h5>
                    </button>
                  </div>
                </div>
                ) : null}
              </div>
            </div>
          </header>
        </>
      ) : (
        /* ตรง index1.html: logo + link_cnt + marquee */
        <header className="mt-3 mb-2">
          <div className="container">
            <div className="box_row justify-content-between d-none d-md-flex align-items-center">
              <a href={getHeaderHref('/')} className="img_logo">
                <img src={branding.logoUrl} alt={branding.siteName} onError={handleImgError} />
              </a>
              <div className="box_row align-items-center gap-2">
                <button
                  type="button"
                  onClick={openPaymentRatesModal}
                  className="link_cnt payment-rates-cta payment-rates-cta--link-cnt text-decoration-none border-0"
                  style={{ cursor: 'pointer' }}
                >
                  <div className="img d-flex align-items-center justify-content-center">
                    <i className="fas fa-coins payment-rates-cta__icon" style={{ fontSize: '1.1rem' }} aria-hidden />
                  </div>
                  <span className="ms-2 payment-rates-cta__label">อัตราการจ่าย</span>
                </button>
                <a href={getHeaderHref('/contact')} className="link_cnt text-decoration-none">
                  <div className="img">
                    <img src={getAssetUrl('/img/line_icon.png')} alt="Contact" onError={handleImgError} />
                  </div>
                  <span className="ms-2">ติดต่อเรา</span>
                </a>
                {authenticated && (
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="link_pmr box_row align-items-center font_rg bg_primary border_10 border-0 px-3 py-2"
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="c_tertiary">ออกจากระบบ</span>
                  </button>
                )}
              </div>
            </div>
            <div className="box_row w-100 py-0 flex-nowrap border_5 bg_secondary p-1 bx_sd mt-2 mt-md-3">
              <div className="ps-2 pe-1 py-2">
                <img
                  src={getAssetUrl('/img/volumeup.svg')}
                  alt="Sound"
                  style={{ width: '70%' }}
                  className="wip_wup"
                  onError={handleImgError}
                />
              </div>
              <marquee
                direction="left"
                onMouseOver={(e: React.MouseEvent<HTMLElement>) => (e.target as HTMLMarqueeElement).stop()}
                onMouseOut={(e: React.MouseEvent<HTMLElement>) => (e.target as HTMLMarqueeElement).start()}
                className="c_secondary"
              >
                {displayMarquee}
              </marquee>
            </div>
          </div>
        </header>
      ))}

      {/* Main Content - HuayToday Style — มี padding-bottom เมื่อมี bottom nav เพื่อให้เลื่อนลงไปอ่านเนื้อหาสุดท้ายได้ */}
      <div className={`pt-2 pb-2 ${authenticated ? 'content-with-bottom-nav' : ''}`}>
        <div className="container">
          <div className="row mx__1">
            {/* หน้าแจ้งฝากเงิน (จองโอน): แสดงเฉพาะเนื้อหา ไม่มีส่วนด้านบน */}
            {isDepositReservePage ? (
              <div className="col-12 px-1">
                {outlet}
              </div>
            ) : isFullWidthPage ? (
              <div className="col-12 px-1">
                {outlet}
              </div>
            ) : (
              <>
            {/* หน้าแรก: แยกแต่ละบล็อกเป็น row เต็มความกว้าง (col-12) — Modern design */}
            {/* แถวที่ 1 — สวัสดีคุณ / ฟอร์มล็อกอิน */}
            <div className="col-12 px-1 mb-3">
              <div className="h_login animateMe" data-animation="fadeInUp">
                <div className={authenticated ? 'home-card' : 'bd_box w-100 border_10 h_m'}>
                  <div className={authenticated ? 'home-card-inner' : 'd-flex flex-wrap align-items-center p-0 p-md-2 bg_op_secondary border_10 bx_sd'}>
                    {/* โหมดยังไม่ login: index.html — มี show_rg (Desktop) + show_lgi (ฟอร์ม) */}
                    {!authenticated && (
                      <div className="show_rg d-none d-md-block">
                        <div className="box_show_user mx-auto">
                          <img
                            src={getAssetUrl('/img/user.svg')}
                            alt="User"
                            onError={handleImgError}
                          />
                        </div>
                        <h4 className="t_show_user t_show_user_mb mt-2 text-center">
                          เข้าสู่ระบบ
                        </h4>
                        <div className="wrap justify-content-center">
                          <Link to="/register" className="link_pmr box_row justify-content-center font_rg bg_gd_tertiary border_10 text-decoration-none">
                            <div className="wip_wup d-flex align-items-center ft_sd c_tertiary font_md">
                              <i className="fas fa-user-plus me-2"></i>
                              <span className="">สมัครสมาชิก</span>
                            </div>
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* โหมดยังไม่ login: ฟอร์มอยู่ใน show_lgi (Desktop 50% / Mobile เต็มความกว้าง) */}
                    {!authenticated && (
                      <div className="show_lgi">
                        <div className="mx-auto d-block d-md-none mb-4" style={{ width: '70%' }}>
                          <img src={branding.logoUrl} alt="" onError={handleImgError} />
                        </div>
                        <form onSubmit={handleLoginSubmit}>
                          {loginError && (
                            <div className="mb-2 small text-danger">{loginError}</div>
                          )}
                          <div className="input_box border_10 mb-2">
                            <div className="icon">
                              <i className="fas fa-user c_secondary"></i>
                            </div>
                            <input
                              type="text"
                              placeholder="ชื่อผู้ใช้ หรือ เบอร์โทรศัพท์"
                              value={loginUsername}
                              onChange={(e) => setLoginUsername(e.target.value)}
                              disabled={loginLoading}
                              autoComplete="username"
                            />
                          </div>
                          <div className="input_box border_10 mb-2">
                            <div className="icon">
                              <i className="fas fa-key c_secondary"></i>
                            </div>
                            <input
                              type="password"
                              placeholder="รหัสผ่าน"
                              value={loginPassword}
                              onChange={(e) => setLoginPassword(e.target.value)}
                              disabled={loginLoading}
                              autoComplete="current-password"
                            />
                          </div>
                          <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                            <label className="d-flex align-items-center font_rg c_tertiary mb-0 me-2" style={{ cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={loginRememberMe}
                                onChange={(e) => setLoginRememberMe(e.target.checked)}
                                disabled={loginLoading}
                                className="me-2"
                              />
                              <span>จำฉันไว้ในระบบ</span>
                            </label>
                            <Link to="/contact" className="font_rg border_10" title="ติดต่อแอดมินเพื่อรีเซ็ตรหัสผ่าน">
                              <span className="c_tertiary"><u>ลืมรหัสผ่าน</u></span>
                            </Link>
                          </div>
                          <button
                            type="submit"
                            className="link_pmr box_row justify-content-center font_rg bg_primary border_10 w-100 border-0"
                            disabled={loginLoading}
                          >
                            <span className="c_tertiary">
                              {loginLoading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
                            </span>
                          </button>
                          <div className="d-block d-md-none">
                            <div className="row mx__1 mt-2">
                              <div className="col-6 px-1">
                                <Link to="/register" className="link_pmr box_row justify-content-center font_rg bg_gd_tertiary border_10 text-decoration-none">
                                  <div className="wip_wup d-flex align-items-center ft_sd c_tertiary font_md">
                                    <i className="fas fa-user-plus me-2 d-block d-md-none d-lg-block"></i>
                                    <span className="">สมัครสมาชิก</span>
                                  </div>
                                </Link>
                              </div>
                              <div className="col-6 px-1">
                                <a href={getHeaderHref('/contact')} className="link_pmr box_row justify-content-center font_rg bg_gd_quaternary border_10 text-decoration-none">
                                  <div style={{ width: 24 }}>
                                    <img src={getAssetUrl('/img/line_icon.png')} alt="" onError={handleImgError} />
                                  </div>
                                  <span className="c_tertiary ms-2">ติดต่อเรา</span>
                                </a>
                              </div>
                              <div className="col-12 px-1 mt-2">
                                <button
                                  type="button"
                                  onClick={openPaymentRatesModal}
                                  className="link_pmr box_row justify-content-center font_rg border_10 border-0 w-100 payment-rates-cta"
                                  style={{ cursor: 'pointer' }}
                                >
                                  <i className="fas fa-coins me-2 payment-rates-cta__icon" aria-hidden />
                                  <span className="payment-rates-cta__label">อัตราการจ่าย</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </form>
                      </div>
                    )}

                    {/* โหมด login แล้ว: แถวที่ 1 — สวัสดีคุณ + เบอร์ (Modern card) */}
                    {authenticated && user && (
                      <div className="home-greeting">
                        <div className="home-greeting-icon">
                          <i className="fas fa-user-circle" aria-hidden />
                        </div>
                        <span className="home-greeting-text">
                          สวัสดีคุณ {user.mobile || user.username || '—'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* แถวที่ 2 — ยอดเครดิต + คะแนน (เมื่อ login) */}
            {authenticated && user && (
              <div className="col-12 px-1 mb-3">
                <div className="home-card">
                  <div className="home-card-inner home-balance-row">
                    <div className="home-balance-block">
                      <div className="home-balance-label">ยอดเครดิตของคุณ</div>
                      <div className="home-balance-value">
                        {isLoadingBalanceAndPoints
                          ? 'กำลังโหลด...'
                          : walletData != null
                            ? walletService.formatCurrency(walletService.getDisplayBalance(walletData)) + ' ฿'
                            : '0.00 ฿'}
                      </div>
                    </div>
                    <div className="home-balance-block" style={{ textAlign: 'right' }}>
                      <div className="home-balance-label">คะแนนของคุณ</div>
                      <div className="home-balance-value">
                        {isLoadingBalanceAndPoints
                          ? 'กำลังโหลด...'
                          : userInfoSummary != null
                            ? userInfoSummary.points.toLocaleString('th-TH', { minimumFractionDigits: 2 }) + ' pts'
                            : '0.00 pts'}
                      </div>
                      <Link to="/points" className="home-balance-link c_warning text-decoration-underline">
                        ดูคะแนนทั้งหมด
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* แถวที่ 3 + 4 — ปุ่มเข้าแทงหวย, เข้าเล่นเกมส์, ออกจากระบบ (เมื่อ login) */}
            {authenticated && (
              <div className="col-12 px-1 mb-3">
                <div className="home-actions">
                  {isLotteryEnabled ? (
                    <Link to="/lobby-huay" className="home-btn home-btn-lottery text-decoration-none">
                      <i className="fas fa-sign-in-alt" aria-hidden />
                      <span>เข้าแทงหวย</span>
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="home-btn home-btn-lottery opacity-60 cursor-not-allowed"
                      disabled
                      title={!tenantLotteryEnabled ? 'ปิดปรับปรุงระบบหวย' : 'ปิดการใช้งานหวยโดยผู้ดูแลระบบ'}
                    >
                      <i className="fas fa-ban" aria-hidden />
                      <span>{!tenantLotteryEnabled ? 'ปิดปรับปรุงระบบ' : 'ปิดแทงหวย'}</span>
                    </button>
                  )}
                  {isCasinoEnabled ? (
                    <Link to="/lobby-casino" className="home-btn home-btn-casino text-decoration-none">
                      <i className="fas fa-gamepad" aria-hidden />
                      <span>เข้าเล่นเกมส์</span>
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="home-btn home-btn-casino opacity-60 cursor-not-allowed"
                      disabled
                      title={!tenantCasinoEnabled ? 'ปิดปรับปรุงระบบเกมส์' : 'ปิดการใช้งานเกมส์โดยผู้ดูแลระบบ'}
                    >
                      <i className="fas fa-ban" aria-hidden />
                      <span>{!tenantCasinoEnabled ? 'ปิดปรับปรุงระบบ' : 'ปิดเข้าเล่นเกมส์'}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="home-btn home-btn-logout"
                  >
                    <i className="fas fa-sign-out-alt" aria-hidden />
                    <span>ออกจากระบบ</span>
                  </button>
                </div>
              </div>
            )}

            {/* แถวผลรางวัล — เต็มความกว้าง (col-12) */}
            <div className="col-12 px-1 mt-2">
              {outlet ?? children}
            </div>

            {/* แถวเต็มความกว้าง ตรง template: หวยชุด, หวยต่างประเทศ — ไม่แสดงในหน้า Dashboard */}
            {!isFullWidthPage && bottomSections.map((section, i) => (
              <div key={i} className="col-12 px-1 mb-3">
                {section}
              </div>
            ))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modern Bottom Navigation — แสดงเมื่อ login (รวมหน้าแรก HTD) */}
      {authenticated && (
        <>
          <div className={`menu_footer_f${isHome ? ' menu_footer_f--htd-home' : ''}`}>
            <div className="navigation">
              <ul>
                {navigationItems.map((item) => (
                  <li
                    key={item.id}
                    className={activeNavItem === item.id ? 'active' : ''}
                    onClick={() => handleNavItemClick(item.id)}
                  >
                    <a href="#" className="text-decoration-none" onClick={(e) => e.preventDefault()}>
                      <span className="icon">
                        <HtdBottomNavIcon id={item.id} />
                      </span>
                      <span className="text">{item.text}</span>
                    </a>
                  </li>
                ))}
                <div className="indicator">
                  <div className="indicator-wrap"></div>
                </div>
              </ul>
            </div>
          </div>

          {/* Backdrop + popup ถอน — แสดงเฉพาะเมื่อระบบเปิดถอนเงิน */}
          {withdrawEnabled && openDWPopup === 'withdraw' && (
            <div
              className="dw_popup_backdrop htd-wd-sheet-backdrop"
              onClick={closeDWPopup}
              onKeyDown={(e) => e.key === 'Escape' && closeDWPopup()}
              role="button"
              tabIndex={0}
              aria-label="ปิด"
            />
          )}

          <BottomDepositFlowModal
            isOpen={openDWPopup === 'deposit'}
            themeConfig={themeConfig}
            assetConfig={assetConfig}
            userBankAccounts={userBankAccounts}
            onClose={closeDWPopup}
            onCompleted={() => {
              void loadBalanceAndPoints()
              loadUserBankAccounts()
            }}
          />

          {/* Popup ถอนเงิน — HTD bottom sheet */}
          {withdrawEnabled && (
          <div
            className={`withdraw_box htd-wd-sheet ${openDWPopup === 'withdraw' ? 'up' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="htd-wd-sheet-title"
            aria-hidden={openDWPopup !== 'withdraw'}
          >
            <div className="htd-wd-sheet__handle" aria-hidden />
            <header className="htd-wd-sheet__head">
              <div>
                <p className="htd-wd-sheet__eyebrow">Withdraw</p>
                <h2 id="htd-wd-sheet-title" className="htd-wd-sheet__title">
                  กรุณาใส่จำนวนที่จะถอน
                </h2>
              </div>
              <button
                type="button"
                className="htd-wd-sheet__close"
                onClick={closeDWPopup}
                aria-label="ปิด"
              >
                <i className="fa fa-times" aria-hidden />
              </button>
            </header>

            <div className="htd-wd-sheet__body">
              {primaryUserBankAccount ? (
                <>
                  <div className="htd-wd-sheet__bank">
                    <div className="htd-wd-sheet__bank-logo">
                      <img
                        src={getUserBankLogoUrl(primaryUserBankAccount)}
                        alt={primaryUserBankAccount.bankName || primaryUserBankAccount.bankCode || 'Bank'}
                        onError={handleImgError}
                      />
                    </div>
                    <div className="htd-wd-sheet__bank-meta">
                      <p className="htd-wd-sheet__bank-name">
                        {primaryUserBankAccount.bankName || primaryUserBankAccount.bankCode || 'บัญชีธนาคาร'}
                      </p>
                      <p className="htd-wd-sheet__bank-line">{primaryUserBankAccount.accountName}</p>
                      <p className="htd-wd-sheet__bank-line htd-wd-sheet__bank-line--mono">
                        {primaryUserBankAccount.accountNumber}
                      </p>
                    </div>
                  </div>

                  <div className="htd-wd-sheet__balance">
                    <span>คงเหลือ</span>
                    <strong>
                      {walletData != null
                        ? walletService.formatCurrency(walletService.getDisplayBalance(walletData)) + ' ฿'
                        : '0.00 ฿'}
                    </strong>
                  </div>

                  <div className="htd-wd-sheet__form">
                    <input
                      type="text"
                      className="htd-wd-sheet__input"
                      placeholder="จำนวนเงินที่ต้องการถอน"
                      inputMode="decimal"
                      value={withdrawAmountInput}
                      onChange={(event) => {
                        const nextValue = event.target.value.replace(/,/g, '')
                        if (nextValue === '' || /^\d*\.?\d{0,2}$/.test(nextValue)) {
                          setWithdrawAmountInput(nextValue)
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          void handleWithdrawPopupSubmit()
                        }
                      }}
                      disabled={isSubmittingWithdrawPopup}
                      aria-label="จำนวนเงินที่ต้องการถอน"
                    />
                    <button
                      type="button"
                      className="htd-wd-sheet__submit"
                      onClick={() => void handleWithdrawPopupSubmit()}
                      disabled={isSubmittingWithdrawPopup}
                    >
                      {isSubmittingWithdrawPopup ? 'กำลังส่ง...' : 'ถอนเงิน'}
                    </button>
                  </div>

                  <Link to="/withdraw" className="htd-wd-sheet__full" onClick={closeDWPopup}>
                    ไปหน้าถอนเงินแบบเต็ม
                  </Link>
                </>
              ) : (
                <div className="htd-wd-sheet__empty">
                  <p>กรุณาเพิ่มบัญชีรับเงินในโปรไฟล์ก่อนถอนเงิน</p>
                  <button
                    type="button"
                    className="htd-wd-sheet__submit htd-wd-sheet__submit--block"
                    onClick={() => {
                      closeDWPopup()
                      navigate('/member')
                    }}
                  >
                    <i className="fa fa-edit" aria-hidden />
                    กรอกข้อมูลเพิ่มเติม
                  </button>
                </div>
              )}
            </div>
          </div>
          )}
        </>
      )}

      {/* Chat Widget - when webChatEnabled and user is authenticated (same as user-classic) */}
      {authenticated && themeConfig.settings?.webChatEnabled === true && (
        <ChatWidget themeConfig={themeConfig} />
      )}

      {/* Live TV float — draggable, default left (Chat stays bottom-right) */}
      {authenticated && (
        <DraggableLiveTvFloat themeConfig={themeConfig} bottomOffset={88} />
      )}

      {/* Prediction float — draggable, default right */}
      {authenticated && (
        <DraggablePredictionFloat themeConfig={themeConfig} bottomOffset={88} />
      )}

      {/* Session idle warning modal (เหมือน user-classic) */}
      {authenticated && (
        <SessionWarningModal
          isOpen={showSessionWarning}
          timeRemaining={timeRemaining}
          onExtendSession={extendSession}
          onLogout={sessionWarningLogout}
        />
      )}

      {/* อัตราการจ่าย — modal ใช้ร่วม desktop/mobile */}
      {showPaymentRatesModal && (
        <InfoModal
          isOpen={showPaymentRatesModal}
          onClose={closePaymentRatesModal}
          title="อัตราการจ่าย"
          content={paymentRates ?? { type: 'text', textContent: '', imageUrl: '' }}
        />
      )}
    </div>
    </OpenDWContext.Provider>
  )
}

export default ModernLayout
